/**
 * Turns the flat leaf rows the rollup endpoints return into a nested list of display rows (group
 * subtotals + indented leaves), so the read-only rollup views can render a clean nested MUI table the
 * same way "By period & node" does — no data-grid component. Group order follows leaf insertion order,
 * so the caller controls sorting by pre-sorting the leaves.
 */

import { cleanFloat } from './planEntryRows';

/** One per-unit quantity bucket. A null unitId is the "unitless" bucket (lines saved without a stamped unit). */
export interface UnitQuantity {
  unitId: string | null;
  quantity: number;
}

export interface RollupLeaf {
  keys: string[];
  amount: number;
  /** Per-unit quantities, sparse — empty when the leaf carries no quantity data. */
  quantities: UnitQuantity[];
}

/** One level or leaf of the row tree, without figures: what a reader whose totals come from the server renders. */
export interface RollupTreeRow {
  /** Unique path (ancestor labels joined) — used for collapse and React keys. */
  path: string;
  depth: number;
  label: string;
  /** True for a grouping level (has children); false for a terminal leaf. */
  isGroup: boolean;
  childCount: number;
}

export interface RollupDisplayRow extends RollupTreeRow {
  amount: number;
  /**
   * Per-unit quantity sums over the subtree, never merged across units ("5 ha + 40 tim" is not a
   * number — spec §12.4). First-appearance order from the leaves, the unitless bucket last.
   */
  quantities: UnitQuantity[];
  /** Weighted-average unit price (amount / quantity) only when the subtree is unit-uniform (one non-zero bucket); null otherwise. */
  unitPrice: number | null;
}

interface Node<T> {
  label: string;
  children: Map<string, Node<T>>;
  leaves: T[];
}

const SEP = '\x01';

/** The path of a level from its labels, outermost first — what `RollupTreeRow.path` holds. */
export function rollupPath(labels: readonly string[]): string {
  return labels.join(SEP);
}

/** Nests leaves by their keys; group order follows leaf insertion order. */
function buildNodes<T extends { keys: string[] }>(leaves: T[]): Node<T> {
  const root: Node<T> = { label: '', children: new Map(), leaves: [] };
  for (const leaf of leaves) {
    let node = root;
    for (const k of leaf.keys) {
      let child = node.children.get(k);
      if (!child) {
        child = { label: k, children: new Map(), leaves: [] };
        node.children.set(k, child);
      }
      node = child;
    }
    node.leaves.push(leaf);
  }
  return root;
}

/** Every level and leaf in display order (a group before its children), each mapped by `row`. */
function walkTree<T, R>(root: Node<T>, row: (node: Node<T>, base: RollupTreeRow) => R): R[] {
  const out: R[] = [];
  function walk(node: Node<T>, depth: number, path: string) {
    for (const child of node.children.values()) {
      const p = path ? `${path}${SEP}${child.label}` : child.label;
      const isGroup = child.children.size > 0;
      out.push(row(child, { path: p, depth, label: child.label, isGroup, childCount: child.children.size }));
      if (isGroup) walk(child, depth + 1, p);
    }
  }
  walk(root, 0, '');
  return out;
}

/** The row tree's levels and leaves alone, for a reader whose totals come from the server (the pivot, §9.3). */
export function buildRollupTree(keys: string[][]): RollupTreeRow[] {
  return walkTree(buildNodes(keys.map((k) => ({ keys: k }))), (_node, base) => base);
}

export function buildRollupRows(leaves: RollupLeaf[]): RollupDisplayRow[] {
  const root = buildNodes(leaves);

  // Per-unit buckets (a JS Map allows the null key — the unitless bucket), mirroring sumByUnit's
  // semantics in planEntryRows: buckets accumulate independently and are NEVER summed with each
  // other. Insertion order = first appearance in leaf order (leaves arrive server-sorted).
  function agg(node: Node<RollupLeaf>): { amount: number; buckets: Map<string | null, number> } {
    let amount = 0;
    const buckets = new Map<string | null, number>();
    const add = (unitId: string | null, quantity: number) =>
      buckets.set(unitId, (buckets.get(unitId) ?? 0) + quantity);
    for (const l of node.leaves) {
      amount += l.amount;
      for (const q of l.quantities) add(q.unitId, q.quantity);
    }
    for (const c of node.children.values()) {
      const r = agg(c);
      amount += r.amount;
      for (const [unitId, quantity] of r.buckets) add(unitId, quantity);
    }
    return { amount, buckets };
  }

  /** Buckets → display list: float-cleaned, first-appearance order, the unitless bucket last. */
  function toQuantities(buckets: Map<string | null, number>): UnitQuantity[] {
    const out: UnitQuantity[] = [];
    for (const [unitId, quantity] of buckets) {
      if (unitId !== null) out.push({ unitId, quantity: cleanFloat(quantity) });
    }
    if (buckets.has(null)) out.push({ unitId: null, quantity: cleanFloat(buckets.get(null) ?? 0) });
    return out;
  }

  return walkTree(root, (node, base) => {
    const a = agg(node);
    const quantities = toQuantities(a.buckets);
    // Pris only where the subtree is unit-uniform: exactly one bucket, non-zero. A mixed-unit
    // average (amount / "5 ha + 40 tim") is not a number.
    const uniform = quantities.length === 1 ? quantities[0].quantity : null;
    return { ...base, amount: a.amount, quantities, unitPrice: uniform ? a.amount / uniform : null };
  });
}

/** Whether a row is hidden because one of its ancestors is collapsed. */
export function isHiddenByCollapse(path: string, collapsed: Set<string>): boolean {
  for (const c of collapsed) {
    if (path !== c && path.startsWith(c + SEP)) return true;
  }
  return false;
}
